import { useState } from "react";
import { TrashIcon, XIcon } from "@phosphor-icons/react";
import { photoUrl } from "../../lib/diagnostics";

export default function PhotoGrid({
  photos,
  api,
  canEdit,
  canDeletePhoto,
  onChange,
}) {
  const [lightbox, setLightbox] = useState(null);
  const [editing, setEditing] = useState(null); // photo id

  if (!photos.length) {
    return (
      <div
        data-testid="photo-grid-empty"
        className="text-xs text-slate-500 py-4"
      >
        No photos uploaded yet.
      </div>
    );
  }

  const updateCaption = async (photo, caption) => {
    try {
      await api.patch(`/diagnostic-photos/${photo.id}`, { caption });
      onChange();
    } catch {
      /* swallow — caller refreshes on close */
    }
    setEditing(null);
  };

  const removePhoto = async (photo) => {
    if (!window.confirm(`Delete photo "${photo.filename}"?`)) return;
    try {
      await api.delete(`/diagnostic-photos/${photo.id}`);
      onChange();
    } catch {
      /* ignore — refresh anyway */
      onChange();
    }
  };

  return (
    <>
      <div
        data-testid="photo-grid"
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
      >
        {photos.map((p) => (
          <figure
            key={p.id}
            data-testid={`photo-card-${p.id}`}
            className="group relative bg-slate-100 border border-slate-200 rounded-md overflow-hidden"
          >
            <button
              type="button"
              className="block w-full aspect-square bg-slate-200"
              onClick={() => setLightbox(p)}
              data-testid={`photo-thumb-${p.id}`}
            >
              <img
                src={photoUrl(p.id, { thumb: true })}
                alt={p.caption || p.filename}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </button>
            {canDeletePhoto && (
              <button
                type="button"
                onClick={() => removePhoto(p)}
                data-testid={`photo-delete-${p.id}`}
                aria-label="Delete photo"
                className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/90 text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-600"
              >
                <TrashIcon size={12} weight="bold" />
              </button>
            )}
            <figcaption className="px-2 py-1.5 bg-white border-t border-slate-200">
              {editing === p.id ? (
                <input
                  type="text"
                  defaultValue={p.caption || ""}
                  autoFocus
                  onBlur={(e) => updateCaption(p, e.target.value || null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.target.blur();
                    if (e.key === "Escape") setEditing(null);
                  }}
                  data-testid={`photo-caption-input-${p.id}`}
                  className="w-full text-[11px] outline-none border-b border-amber-400"
                />
              ) : (
                <div
                  onClick={() => canEdit && setEditing(p.id)}
                  data-testid={`photo-caption-${p.id}`}
                  className={`text-[11px] truncate ${
                    canEdit
                      ? "cursor-text text-slate-700 hover:text-slate-900"
                      : "text-slate-700"
                  }`}
                  title={p.caption || "(no caption — click to add)"}
                >
                  {p.caption || (canEdit ? "(add caption)" : "—")}
                </div>
              )}
              <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5 font-data">
                {p.uploaded_by_name || "tech"} ·{" "}
                {new Date(p.uploaded_at).toLocaleDateString()}
              </div>
            </figcaption>
          </figure>
        ))}
      </div>

      {lightbox && (
        <div
          data-testid="photo-lightbox"
          className="fixed inset-0 z-50 bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            data-testid="photo-lightbox-close"
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-md bg-white/90 text-slate-900 hover:bg-white"
          >
            <XIcon size={16} weight="bold" />
          </button>
          <img
            src={photoUrl(lightbox.id)}
            alt={lightbox.caption || lightbox.filename}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-md shadow-card"
          />
          {lightbox.caption && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-2 bg-slate-900/80 text-white text-sm rounded-md max-w-[85vw] text-center"
            >
              {lightbox.caption}
            </div>
          )}
        </div>
      )}
    </>
  );
}
